import { useMemo } from 'react'
import { mapValueKey } from '@editorUtils'
import { reduce, filter } from 'lodash-es'

interface Options {
  verticalTransmissionGraph: VerticalTransmissionGraph | null
  loads: ElementLoad[] | null
  domains: Domain[] // Make sure to pass in all domains (also for Positions)
  slabBeams: SlabBeam[]
}

const useEnhanceLoadsWithDomain = ({
  verticalTransmissionGraph,
  loads,
  domains,
  slabBeams,
}: Options): (AreaLoad | DomainLoad)[] | undefined => {
  const guidToDomain = useMemo(() => mapValueKey(domains), [domains])

  // trying to map load domain with element
  const targetsWithDomain = useMemo(
    () =>
      verticalTransmissionGraph &&
      filter(verticalTransmissionGraph.element_targets, ({ domain }) => !!domain),
    [verticalTransmissionGraph],
  )

  const elementTargetDomains = useMemo(
    () =>
      targetsWithDomain &&
      reduce(
        targetsWithDomain,
        (collector, target) => ({
          ...collector,
          [target.domain_guid]: target.domain,
        }),
        {} as Record<string, Domain | null>,
      ),
    [targetsWithDomain],
  )

  const finalLoads = useMemo(() => {
    if (!loads || !elementTargetDomains) return []
    const guidToSlabBeam = mapValueKey(slabBeams)

    return loads.map(load => {
      // trying to map load domain with slab member
      const slabBeam = guidToSlabBeam[load.element_guid]

      if (slabBeam) {
        return {
          ...load,
          domain: {
            start: slabBeam.shape.start,
            end: slabBeam.shape.end,
            coordinate_system: slabBeam.coordinate_system,
            length: slabBeam ? slabBeam.shape.end.sub(slabBeam.shape.start).length() : 0,
            guid: load.domain_guid,
          },
        }
      }

      const domain = elementTargetDomains[load.domain_guid] || guidToDomain[load.domain_guid]

      return {
        ...load,
        domain,
      }
    })
  }, [loads, elementTargetDomains, slabBeams, guidToDomain])

  return finalLoads
}

export { useEnhanceLoadsWithDomain }
